"use client";

import { css, styled } from "styled-components";

export const BodyCell = styled.div<{
  $maxWidth?: string;
  $minWidth?: string;
  $isTooltipVisible: boolean;
  $isDisableEdit?: boolean;
}>`
  display: flex;
  max-width: ${({ $maxWidth }) => $maxWidth};
  min-width: ${({ $minWidth }) => $minWidth};
  padding: var(--Spacing-8, 0.8rem) var(--Spacing-4, 0.4rem);
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  gap: var(--Spacing-4, 0.4rem);
  flex: 1 0 0;

  position: relative;

  border-right: 1px solid var(--Color-Common-Border-Tertiary, #e5e7eb);

  &:last-child {
    border-right: none;
  }

  &:hover:not($isDisableEdit) {
    background: var(
      --Color-Common-Bg-Interactive-Primary-Subtle-Hovered,
      #e0f4ff
    );
  }
`;

export const BodyCellValue = styled.div<{
  $isDisplayBullet: boolean;
  $isDisableEdit?: boolean;
}>`
  display: block;
  padding: var(--Spacing-2, 0.2rem) var(--Spacing-6, 0.6rem);
  align-items: flex-start;
  gap: var(--Spacing-2, 0.2rem);
  align-self: stretch;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: var(--Color-Common-Text-Primary, #2a2f37);
  font-family: var(--font-pt-serif);
  font-size: 1.2rem;
  font-weight: 400;
  line-height: 130%;
  width: 100%;

  ${({ $isDisplayBullet }) =>
    $isDisplayBullet &&
    css`
      padding: var(--Spacing-2, 0.2rem) var(--Spacing-2, 0.2rem)
        var(--Spacing-2, 0.2rem) var(--Spacing-0, 0rem);
    `}

  ${({ $isDisableEdit }) =>
    $isDisableEdit &&
    css`
      color: var(--Color-Common-Text-Disabled, #96a1b0);
    `}
`;

const TOOLTIP_TRANSFORM = {
  center: css`
    transform: translateX(-50%);
  `,
  left: css`
    transform: translateX(0);
  `,
  right: css`
    transform: translateX(-100%);
  `,
};

export const Tooltip = styled.div<{
  $tooltipAlignment: "left" | "center" | "right";
  $isTooltipVisible: boolean;
  $currentTooltipPosition: {
    top: number;
    left: number;
  };
}>`
  border-radius: var(--Border-Radius-4, 0.4rem);
  border: 1px solid var(--Color-Common-Border-Tertiary, #e5e7eb);
  background: var(--Color-Common-Bg-Primary, #fff);

  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.16),
    0px 0px 2px 0px rgba(0, 0, 0, 0.12);

  display: flex;
  padding: var(--Spacing-4, 0.4rem) var(--Spacing-8, 0.8rem);
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: var(--Spacing-4, 0.4rem);

  width: max-content;
  max-width: 73.6rem;

  position: fixed;
  top: ${({ $currentTooltipPosition }) => $currentTooltipPosition.top}px;
  left: ${({ $currentTooltipPosition }) => $currentTooltipPosition.left}px;

  visibility: ${({ $isTooltipVisible }) =>
    $isTooltipVisible ? "visible" : "hidden"};

  ${({ $tooltipAlignment }) => TOOLTIP_TRANSFORM[$tooltipAlignment]}

  z-index: 9999;
  color: var(--Color-Common-Text-Primary, #2a2f37);
  font-family: var(--font-pt-serif);
  font-size: 1.2rem;
  font-weight: 400;
  line-height: 130%;

  justify-content: space-between;
  align-items: center;
`;

export const ListRow = styled.div<{ $isDisplayBullet: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  position: relative;

  ${({ $isDisplayBullet }) =>
    $isDisplayBullet &&
    css`
      padding-left: 1.2rem;

      &::before {
        position: absolute;
        left: 0.4rem;
        top: 0.4rem;
        display: block;
        content: "•";
        color: var(--Color-Common-Text-Primary, #2a2f37);
        font-size: 1rem;
      }
    `}
`;

export const TooltipValue = styled.div<{
  $isDisplayBullet: boolean;
}>`
  display: flex;
  padding: var(--Spacing-2, 0.2rem) var(--Spacing-6, 0.6rem);
  align-items: flex-start;
  gap: var(--Spacing-2, 0.2rem);
  align-self: stretch;

  color: var(--Color-Common-Text-Primary, #2a2f37);

  font-family: var(--font-pt-serif);
  font-size: 1.2rem;
  font-weight: 400;
  line-height: 130%;

  ${({ $isDisplayBullet }) =>
    $isDisplayBullet &&
    css`
      padding: var(--Spacing-2, 0.2rem) var(--Spacing-2, 0.2rem)
        var(--Spacing-2, 0.2rem) var(--Spacing-0, 0rem);
    `}
`;

export const CountText = styled.span`
  color: var(--Color-Common-Text-Tertiary, #7c899c);
  font-family: var(--font-pt-serif);
  font-size: 1rem;
  font-weight: 700;
  line-height: 130%;
  padding-left: 1.2rem;
`;

export const ToolTipIconWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

export const IconWrapper = styled.div<{ $disabled?: boolean }>`
  display: flex;
  width: 2rem;
  height: 2rem;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};

  &:hover {
    ${({ $disabled }) =>
      !$disabled &&
      css`
        svg {
          path {
            fill: #015d8e;
          }
        }
      `}
  }

  &:active {
    ${({ $disabled }) =>
      !$disabled &&
      css`
        svg {
          path {
            fill: #015d8e;
          }
        }
      `}
  }
`;
